import './CalendarStatistic.scss'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { RadioButton } from 'primereact/radiobutton'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

// local imports
import loaderLogo from '../../assets/icons/loading-statistics.png'
import { ICalendarStatistic } from '../../types/CalendarType'
import { IEmployeeSummary } from '../../types/Employee'
import CalendarService from '../../service/CalendarService'
import useDidMountEffect from '../../utils/useDidMountEffect'
import Loader from 'components/ui/Loader'
import CalendarStatisticChartCard from './calendar-statistic/CalendarStatisticChartCard'

const CalendarStatistic = forwardRef((props: { employeeSummary: IEmployeeSummary }, ref) => {
  const { t } = useTranslation()
  const text1 = t('tabs.company-work')
  const text2 = t('tabs.shared-office')
  const text3 = t('tabs.remote-work')
  const text4 = t('most-used-words.all-reservations')
  const text5 = t('most-used-words.no-transaction')
  const mySquadText = 'calendar.my-squad'
  const myTeamText = 'calendar.my-team'
  const errorText = t('overlaypanel-texts.error-text')

  let [month, setMonth] = useState(moment())
  let [statistics, setStatistics] = useState({} as ICalendarStatistic)
  const [radioState, setRadioState] = useState(myTeamText)
  const [showLoader, setShowLoader] = useState(false)
  const [showError, setShowError] = useState<boolean>(false)

  useDidMountEffect(() => {
    getCalendarStatistics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, radioState])

  useEffect(() => {
    if (props.employeeSummary) {
      if (props.employeeSummary.isManager) {
        setRadioState(mySquadText)
      } else {
        setRadioState(myTeamText)
      }
    }
  }, [props.employeeSummary])

  const getMonthlyStatisticText = () => {
    return (
      <span>
        {t('date-terms.monthly')} "{t(radioState)}" {t('calendar.statistic')}
      </span>
    )
  }

  useImperativeHandle(ref, () => ({
    reloadStatisticData() {
      getCalendarStatistics()
    },
  }))

  let getCalendarStatistics = () => {
    if (radioState === mySquadText) {
      getSquadCalendarStatistics()
    } else if (radioState === myTeamText) {
      getTeamCalendarStatistics()
    }
  }

  let getStartAndEndDate = () => {
    return {
      startOfMonth: month.startOf('month').format('YYYY-MM-DD'),
      endOfMonth: month.endOf('month').format('YYYY-MM-DD'),
    }
  }

  const getSquadCalendarStatistics = () => {
    setShowLoader(true)
    setShowError(false)
    let { startOfMonth, endOfMonth } = getStartAndEndDate()
    CalendarService.getManagerCalendarStatistics(startOfMonth, endOfMonth)
      .then((response) => {
        setStatistics(response.data)
        setShowLoader(false)
      })
      .catch((e) => {
        console.log(e)
        setShowLoader(false)
        setShowError(true)
      })
  }

  const getTeamCalendarStatistics = () => {
    setShowLoader(true)
    setShowError(false)
    let { startOfMonth, endOfMonth } = getStartAndEndDate()
    CalendarService.getCalendarStatistics(startOfMonth, endOfMonth)
      .then((response) => {
        setStatistics(response.data)
        setShowLoader(false)
      })
      .catch((e) => {
        if (!e.response && !e.request) console.log(e)
        setShowLoader(false)
        setShowError(true)
      })
  }

  let previousMonth = () => {
    setMonth(month.clone().subtract(1, 'months'))
  }

  let nextMonth = () => {
    setMonth(month.clone().add(1, 'months'))
  }

  const renderMonthLabel = () => {
    return <p className="month-label">{month.locale(i18next.language).format('MMMM YYYY')}</p>
  }

  const renderStatistics = () => {
    return (
      <div className="d-flex justify-content-between">
        <CalendarStatisticChartCard
          labelText={text1}
          knobValue={statistics.tableReservationPercentage}
          valueColor={'rgb(58,178,222)'}
        />
        <CalendarStatisticChartCard
          labelText={text2}
          knobValue={statistics.otherLocationTableReservationPercentage}
          valueColor={'rgb(50, 137, 106)'}
        />
        <CalendarStatisticChartCard
          labelText={text3}
          knobValue={statistics.homeOfficeReservationPercentage}
          valueColor={'rgb(249, 192, 107)'}
        />
        <div className="vertical-line"></div>
        <CalendarStatisticChartCard
          labelText={text4}
          knobValue={statistics.allReservationPercentage}
          valueColor={'rgb(88, 104, 184)'}
        />
        <CalendarStatisticChartCard
          labelText={text5}
          knobValue={statistics.unoccupiedDaysPercentage}
          valueColor={'rgb(202, 211, 252)'}
        />
      </div>
    )
  }

  const renderErrorMessage = () => {
    //Error notification
    return (
      <div
        className={'h-100'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}>
        <div
          style={{
            textAlign: 'center',
          }}>
          <h3>{errorText}</h3>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={'col-md-12 of-calendar-statistics '} style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="row">
          <div className="of-calendar-statistic-header">
            <div className="of-calendar-statistic-choice-menu">
              {getMonthlyStatisticText()}

              {props.employeeSummary?.isManager ? (
                <div className="p-field-radiobutton">
                  <RadioButton
                    inputId="mySquad"
                    name="mySquad"
                    value={'calendar.my-squad'}
                    onChange={(e) => setRadioState(e.value)}
                    checked={radioState === 'calendar.my-squad'}
                  />
                  <label htmlFor="mySquad">{t(mySquadText)}</label>
                </div>
              ) : null}
              <div className="p-field-radiobutton">
                <RadioButton
                  inputId="myTeam"
                  name="myTeam"
                  value={'calendar.my-team'}
                  onChange={(e) => setRadioState(e.value)}
                  checked={radioState === 'calendar.my-team'}
                />
                <label htmlFor="myTeam">{t(myTeamText)}</label>
              </div>
            </div>

            <div className="of-calendar-statistic-change-month d-flex justify-content-end">
              {renderMonthLabel()}
              <i id="statisticsPrev" className="arrow pi pi-angle-left" onClick={previousMonth} />
              <i id="statisticsNext" className="arrow pi pi-angle-right" onClick={nextMonth} />
            </div>
          </div>
        </div>
        <div className="row pb-3" style={{ flex: 1 }}>
          {!showLoader && !showError ? (
            renderStatistics()
          ) : showLoader ? (
            <Loader src={loaderLogo} />
          ) : showError ? (
            renderErrorMessage()
          ) : null}
        </div>
      </div>
    </>
  )
})

export default CalendarStatistic
